/**
 * React Hook for AI Agents Integration
 * Provides easy access to MCP AI agents from React components
 */

import { useState, useCallback } from 'react';
import { api } from '../utils/api';

export interface AgentRequest {
  agentId: string;
  action: 'analyze' | 'optimize' | 'generate' | 'execute';
  parameters?: Record<string, any>;
  context?: Record<string, any>;
  useOpenAI?: boolean;
}

export interface AgentResponse {
  agentId: string;
  action: string;
  success: boolean;
  result?: Record<string, any>;
  openAIResponse?: string;
  error?: string;
  executionTime: number;
}

export interface AIAgent {
  id: string;
  name: string;
  description: string;
  tools: string[];
  capabilities: Array<{
    name: string;
    description: string;
    category?: string;
  }>;
}

export const useAIAgents = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<AIAgent[]>([]);

  /**
   * Execute an AI agent
   */
  const executeAgent = useCallback(async (request: AgentRequest): Promise<AgentResponse | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/ai-agents/execute', request);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to execute agent');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Analyze assistant performance
   */
  const analyzeAssistant = useCallback(async (assistantId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/ai-agents/analyze-assistant', { assistantId });
      return response.data.analysis;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to analyze assistant');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Debug assistant issues
   */
  const debugAssistant = useCallback(async (assistantId: string, errorLogs?: any[]) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/ai-agents/debug-assistant', { 
        assistantId, 
        errorLogs 
      });
      return response.data.debugInfo;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to debug assistant');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Generate assistant improvements
   */
  const improveAssistant = useCallback(async (assistantConfig: any) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/ai-agents/improve-assistant', { 
        assistantConfig 
      });
      return response.data.improvements;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate improvements');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Generate business insights
   */
  const generateInsights = useCallback(async (assistantId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/ai-agents/insights/${assistantId}`);
      return response.data.insights;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate insights');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Predict project outcomes
   */
  const predictOutcomes = useCallback(async (projectId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/ai-agents/predict/${projectId}`);
      return response.data.predictions;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to predict outcomes');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List available AI agents
   */
  const listAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/ai-agents/list');
      setAgents(response.data.agents);
      return response.data.agents;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to list agents');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check MCP server health
   */
  const checkHealth = useCallback(async () => {
    try {
      const response = await api.get('/ai-agents/health');
      return response.data.mcpServerHealthy;
    } catch {
      return false;
    }
  }, []);

  /**
   * Execute multiple agents in parallel
   */
  const executeBatch = useCallback(async (requests: AgentRequest[]): Promise<AgentResponse[]> => {
    setLoading(true);
    setError(null);
    
    try {
      const promises = requests.map(request => 
        api.post('/ai-agents/execute', request)
      );
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    } catch (err: any) {
      setError('Failed to execute batch');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    agents,
    executeAgent,
    analyzeAssistant,
    debugAssistant,
    improveAssistant,
    generateInsights,
    predictOutcomes,
    listAgents,
    checkHealth,
    executeBatch
  };
};

// Predefined agent configurations for easy use
export const AI_AGENTS = {
  CODE_REVIEW: 'code-review-ui-enhancement',
  DEBUGGER: 'debugger',
  MINDMAP: 'mindmap-integration-architect',
  SEO: 'seo-performance-optimizer',
  UX: 'ux-polish-specialist'
} as const;

export const AGENT_ACTIONS = {
  ANALYZE: 'analyze',
  OPTIMIZE: 'optimize',
  GENERATE: 'generate',
  EXECUTE: 'execute'
} as const;